package com.macrofocus.eflux.time

import com.macrofocus.eflux.toRawString
import korlibs.time.*

/**
 * Definition:
 * display: the date displayed in the calendar
 * earliest: the earliest date that can be selected in the calendar
 * latest: the latest date that can be selected in the calendar
 * span: the time span covered by the slider
 * min: the date at the beginning of the slider
 * max: the date at the end of the slider
 * lag: the lag (negative) selected through the slider
 * new: the indicator signalling that new data are available
 *
 * Initial state
 * - earliest = latest - 14 days
 * - display = latest
 * - span = 24 hours
 * - min = max(latest - span, earliest)
 * - max = latest
 * - lag = 0
 * - live = on
 * - new = off
 *
 * When new data are received:
 * - latest = now
 * - earliest = latest - 14 days
 * - if(live) {
 *       display = latest + lag
 *       max = max(display, earliest + span)
 *       min = max(max - span, earliest)
 *   }
 * - new = !live && display != latest
 *
 * When data are inputted in calendar:
 * - live = off
 * - display = input
 * - max = max(display, earliest + span)
 * - min = max(max - span, earliest)
 * - lag = min(display - earliest, 0)
 * - new = !live && display != latest
 *
 * When live is turned on:
 * - live = on
 * - display = latest
 * - max = max(display, earliest + span)
 * - min = max(max - span, earliest)
 * - lag = (min(display - max, 0.minutes) / lagUnit.unit)
 * - new = false
 *
 * When Time slider is moved
 * - lag = slider
 * - display = max + lag
 *
 * On each animation step
 * - lag = current
 * - display = max + (lagUnit.unit * lag)
 */
class EFluxTimeTracker(date: DateTime, history: TimeSpan? = null, val span: TimeSpan) : AbstractTimeTracker(date, history) {
    var requestStopAnimation = false

    /**
     * Initial state
     * - earliest = latest - 14 days
     * - display = latest
     * - span = 24 hours
     * - min = max(latest - span, earliest)
     * - max = latest
     * - lag = 0
     * - live = on
     * - new = off
     */
    init {
    }

    override fun setEarliestLatest(earliest: DateTime, latest: DateTime) {
        if(DEBUG) println("setEarliestLatest(${earliest.toRawString()}-${latest.toRawString()})")
        this.latest = latest
        adjustEarliest(earliest)

        newDataReceived(latest)
//        maxDateTime.value = masterDateTime.value
//        minDateTime.value = max(maxDateTime.value - span, earliestDateTime.value)
//        lag.value = (min(masterDateTime.value - earliestDateTime.value, 0.minutes) / lagUnit.value.unit).toInt()
    }

    fun adjustEarliest(date: DateTime) {
        if(DEBUG) println("adjustEarliest(${date.toRawString()})")
        if (history != null) {
            this.earliest = max(date, latest - history)
        } else {
            this.earliest = date
        }
    }

    /**
     * When new data are received:
     * - latest = now
     * - earliest = latest - 14 days
     * - if(live) {
     *       display = latest + lag
     *       max = max(display, earliest + span)
     *       min = max(max - span, earliest)
     *   }
     * - new = !live && display != latest
     */
    override fun newDataReceived(now: DateTime) {
        if(DEBUG) println("newDataReceived(${now.toRawString()})")
        if (now > this.now) {
            this.now = now
            latest = this.now
            adjustEarliest(earliest)
            if (live) {
                master = latest + (lagUnit.unit * masterLag)
                max = max(master, earliest + span)
                min = max(max - span, earliest)
            }
            new = !live && master != latest
        }
    }

    /**
     * When data are inputted in calendar:
     * - live = off
     * - display = input
     * - min = min(max(display, earliest), latest - span)
     * - max = max(min + span, earliest + span)
     * - lag = min(display - max, 0)
     * - new = !live && display != latest
     */
    override fun dateInputted(input: DateTime, turnLiveOff: Boolean, force: Boolean) {
        if(DEBUG) println("dateInputted(${input.toRawString()})")
        if(!force && input < earliest) return
        if(turnLiveOff) {
            live = false
        }
        master = input
        if(force) {
            max = max(master, earliest + span)
            min = max(max - span, earliest)
//            min = min(master, latest - span)
//            max = min + span
        } else {
            max = max(master, earliest + span)
            min = max(max - span, earliest)
//            min = min(max(master, earliest), latest - span)
//            max = max(min + span, earliest + span)
        }
//        masterLag = (min(master - max, 0.minutes) / lagUnit.unit).toInt()
        masterLag = 0
        new = !live && master != latest
    }

    /**
     * When live is turned on:
     * - live = on
     * - display = latest
     * - max = max(display, earliest + span)
     * - min = max(max - span, earliest)
     * - lag = (min(display - max, 0.minutes) / lagUnit.unit)
     * - new = false
     */
    override fun liveTurnedOn() {
        if(DEBUG) println("liveTurnedOn()")
        requestStopAnimation = true
        live = true
        master = latest
        max = max(master, earliest + span)
        min = max(max - span, earliest)
        masterLag = (min(master - max, 0.minutes) / lagUnit.unit).toInt()
        new = false

        reset?.invoke()
    }

    /**
     * When replay is turned on:
     * - live = off
     * - display = max(display - span, earliest)
     * - min = display
     * - max = max(min + span, earliest + span)
     * - lag = min(display - max, 0)
     */
    override fun replayTurnedOn() {
        if(DEBUG) println("replayTurnedOn()")
        live = false
        master = max(master - span, earliest)
        min = master
        max = max(min + span, earliest + span)
        masterLag = (min(master - max, 0.minutes) / lagUnit.unit).toInt()
    }

    /**
     * When Time slider is moved
     * - lag = slider
     * - display = max + lag
     */
    override fun timeSliderMoved(slider: Int) {
        if(DEBUG) println("timeSliderMoved(${slider})")
        displayLag = slider
        val timeSpan = lagUnit.unit * displayLag
        val display = max + timeSpan
        if(DEBUG) println("timeSliderMoved: ${max.toRawString()} + ${timeSpan.toTimeString()} = ${master.toRawString()}")
        this.display = display
    }

    override fun timeSliderReleased(slider: Int) {
        if(DEBUG) println("timeSliderReleased(${slider})")
        live = false
        masterLag = slider
        val timeSpan = lagUnit.unit * masterLag
        val master = max + timeSpan
        if(DEBUG) println("timeSliderReleased: ${max.toRawString()} + ${timeSpan.toTimeString()} = ${master.toRawString()}")
        this.master = master
    }

    /**
     * On each animation step
     * - lag = current
     * - display = max + lag
     */
    override fun stepAnimation(current: Int) : Boolean {
        if (DEBUG) println("stepAnimation(${current})")
        if (requestStopAnimation) {
            requestStopAnimation = false
            return true
        } else {
            masterLag = current
            master = max + (lagUnit.unit * masterLag)
            return false
        }
    }
}